import React, { ReactNode } from 'react'
import { usePermissions } from '../../contexts/PermissionContext'
import {  Typography, Alert } from '@mui/material'
import { Lock } from '@mui/icons-material'

interface PermissionGuardProps {
  children: ReactNode
  permission?: string
  role?: string
  roles?: string[]
  resource?: string
  action?: string
  requireAll?: boolean
  fallback?: ReactNode
  showError?: boolean
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  role,
  roles,
  resource,
  action,
  requireAll = false,
  fallback,
  showError = false
}) => {
  const permissions = usePermissions()

  const hasAccess = (): boolean => {
    const checks: boolean[] = []

    // Check specific permission
    if (permission) {
      checks.push(permissions.hasPermission(permission))
    }

    // Check resource + action permission
    if (resource && action) {
      checks.push(permissions.hasResourcePermission(resource, action))
    }

    // Check specific role
    if (role) {
      checks.push(permissions.hasRole(role))
    }

    // Check multiple roles
    if (roles && roles.length > 0) {
      checks.push(permissions.hasAnyRole(roles))
    }

    // If no checks specified, allow access
    if (checks.length === 0) {
      return true
    }

    // Return based on requireAll flag
    return requireAll ? checks.every(Boolean) : checks.some(Boolean)
  }

  if (!hasAccess()) {
    if (fallback) {
      return <>{fallback}</>
    }

    if (showError) {
      return (
        <Alert severity="warning" icon={<Lock />}>
          <Typography variant="body2">
            You don't have permission to access this content.
          </Typography>
        </Alert>
      )
    }

    return null
  }

  return <>{children}</>
}

// Convenience components for common use cases
export const AdminOnly: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <PermissionGuard roles={['super_admin', 'admin']} fallback={fallback}>
    {children}
  </PermissionGuard>
)

export const SuperAdminOnly: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <PermissionGuard role="super_admin" fallback={fallback}>
    {children}
  </PermissionGuard>
)

export const RequirePermission: React.FC<{ 
  permission: string
  children: ReactNode
  fallback?: ReactNode 
}> = ({ permission, children, fallback }) => (
  <PermissionGuard permission={permission} fallback={fallback}>
    {children}
  </PermissionGuard>
)

export const RequireResource: React.FC<{ 
  resource: string
  action: string
  children: ReactNode
  fallback?: ReactNode 
}> = ({ resource, action, children, fallback }) => (
  <PermissionGuard resource={resource} action={action} fallback={fallback}>
    {children}
  </PermissionGuard>
)